import { Correct } from '../../components/Correct'
import { formatDate, formatMoney, formatRelative } from '../../lib/format'
import type { OutreachItem } from '../../types/outreach'

// The queue: five at a time, each row a sentence a partner would say out loud. The sentence is written by
// the API and names its own evidence; nothing here dresses it up.
//
// The second line is the ENTRY POINT: the opening line the brief wrote, on the row, so a partner with ten
// minutes has somewhere to start without opening anything (owner, 2026-09-17). It appears only where a
// brief is on file and the file earned an opener — never a suggestion Scout made up.

/** The correction's entity id: a reason is derived on every read, so it is named by person and kind. */
export const reasonId = (item: OutreachItem) => `${item.person.personId}:${item.reasonKind}`

export function Queue({ items, openId, onOpen }: { items: OutreachItem[]; openId: string | null; onOpen: (personId: string) => void }) {
  return (
    <ol className="outreach-queue">
      {items.map((item) => <Row key={item.person.personId} item={item} open={openId === item.person.personId} onOpen={onOpen} />)}
    </ol>
  )
}

function Row({ item, open, onOpen }: { item: OutreachItem; open: boolean; onOpen: (personId: string) => void }) {
  const p = item.person
  return (
    <li className={`outreach-row ${item.reasonKind} ${open ? 'open' : ''}`}>
      <button type="button" className="outreach-open" onClick={() => onOpen(p.personId)} aria-expanded={open}>
        <p className="outreach-sentence">
          <strong>{p.name}</strong>
          {p.title && <span className="muted">, {p.title}</span>}
          <span className="muted">{p.title ? ' at ' : ', '}{item.company}</span>
          {' — '}
          <span className="outreach-reason">{item.reason}</span>
        </p>
        {item.brief?.opener && (
          <p className="outreach-opener">
            <span className="kicker">Open with</span>
            <q>{item.brief.opener}</q>
            {item.brief.openerWhy && <span className="muted small">{item.brief.openerWhy}</span>}
          </p>
        )}
        <p className="outreach-meta">
          {item.brief && (
            <span className={`chip brief ${item.brief.stale ? 'stale' : ''}`}
              title={`${item.brief.whoTheyAre} Written ${formatDate(item.brief.writtenAt)}${item.brief.stale ? '; newer mail has arrived since.' : '.'}`}>
              {item.brief.stale ? 'brief ready · newer mail since' : 'brief ready'}
            </span>
          )}
          {item.band && <span className="chip band" title={`${item.band.basis} Cadence: ${item.band.cadence}.`}>{item.band.label} · {item.band.cadence}</span>}
          {item.band && item.yourMark && (
            <span className="chip band yours" title={`${item.yourMark.source}${item.yourMark.note ? ` — your note: ${item.yourMark.note}` : ''}${item.yourMark.yourMark ? ` — your mark: ${item.yourMark.yourMark}` : ''}`}>your band</span>
          )}
          {item.acquaintance.verdict === 'imported' && <span className="chip off" title={item.acquaintance.detail}>from a list, not a relationship</span>}
          {item.openPursuit && (
            <span className="chip pursuit">
              {item.openPursuit.stage} · {item.openPursuit.subject}
              {item.openPursuit.amount != null && ` · ${formatMoney(item.openPursuit.amount)}`}
            </span>
          )}
          <span className="outreach-touch">
            <span title="the last thing you sent them">you: {item.lastOutbound ? formatRelative(item.lastOutbound) : 'never'}</span>
            <span title="the last thing they sent you">them: {item.lastInbound ? formatRelative(item.lastInbound) : 'nothing on file'}</span>
            {item.lastMeeting && <span title="the last time you met">met: {formatRelative(item.lastMeeting)}</span>}
          </span>
        </p>
        {item.secondary.length > 0 && (
          <p className="outreach-also">
            <span className="kicker">also</span>
            {item.secondary.map((c) => <span key={c.kind} className="chip also" title={c.detail}>{c.label}</span>)}
          </p>
        )}
      </button>
      <span className="outreach-correct">
        <Correct
          kind="outreach-reason"
          entityId={reasonId(item)}
          entityLabel={`${p.name} — ${item.reason}`}
          currentValue={item.reasonKind}
          compact
        />
        {item.yourMark && (
          <Correct
            kind="person-mark"
            entityId={p.personId}
            entityLabel={`${p.name} — your band`}
            currentValue={item.yourMark.band}
            compact
          />
        )}
      </span>
    </li>
  )
}
